/**
 * BoardBroadcast — read-only spectator HUD designed for OBS / streaming
 * overlays. No drag-drop, no click-to-play. Both players' resources side
 * by side, round + turn-state up top, score-pip rail down the middle.
 *
 * URL params:
 *   ?overlay=1  → transparent background + minimal chrome (chroma-key
 *                 friendly for layering over a stream backdrop)
 *   ?cam=p1     → focus the player-1 hand strip; `cam=p2` for player-2.
 *                 Without `cam`, both hands shown side-by-side.
 *
 * For tournament casters: drop the URL into a Browser Source in OBS
 * with the desired query params.
 */

import type { ReactElement } from 'react';
import type { CardData, PlayerId, PublicTableState, RealmName, RoundPip } from '../types.js';

type Props = {
  state: PublicTableState;
  viewer: PlayerId;
  cards: ReadonlyMap<string, CardData>;
};

const ROUNDS = 7;

const STONE_TEXTURE: Record<RealmName, string> = {
  Petraia: '/assets/wonders/stone-petraia.png',
  Solfera: '/assets/wonders/stone-solfera.png',
  Thalwind: '/assets/wonders/stone-thalwind.png',
  Umbrathene: '/assets/wonders/stone-umbrathene.png',
  Heliosynth: '/assets/wonders/stone-heliosynth.png',
  Boundless: '/assets/wonders/stone-boundless.png',
  Clear: '/assets/wonders/stone-clear.png',
};

export function BoardBroadcast({ state, viewer, cards }: Props): ReactElement {
  const url = new URL(typeof window !== 'undefined' ? window.location.href : 'http://localhost');
  const overlay = url.searchParams.get('overlay') === '1';
  const cam = url.searchParams.get('cam');

  const [p1, p2] = state.players;
  // For broadcast we always show p1 as left, p2 as right — viewer
  // perspective doesn't apply since the caster is neither player.
  const p1Resources = state.resources?.[p1] ?? { energy: 0, actions: 0, hoard: 0 };
  const p2Resources = state.resources?.[p2] ?? { energy: 0, actions: 0, hoard: 0 };
  const p1Pips = state.roundPips?.[p1] ?? (Array(ROUNDS).fill(null) as RoundPip[]);
  const p2Pips = state.roundPips?.[p2] ?? (Array(ROUNDS).fill(null) as RoundPip[]);
  const p1Score = p1Pips.reduce<number>((a, x) => a + (x === 1 ? 1 : 0), 0);
  const p2Score = p2Pips.reduce<number>((a, x) => a + (x === 1 ? 1 : 0), 0);

  // Hand counts (broadcast view never reveals contents — fog-of-war for both).
  const p1Hand = handCount(state, p1);
  const p2Hand = handCount(state, p2);
  const p1Deck = zoneCount(state, p1, 'deck');
  const p2Deck = zoneCount(state, p2, 'deck');

  const containerClass = overlay
    ? 'flex h-screen w-screen flex-col text-white'
    : 'flex h-screen w-screen flex-col bg-stone-950 text-white';

  return (
    <div className={containerClass}>
      {/* Top bar — match meta */}
      <header className="flex items-center justify-between px-4 py-2 backdrop-blur">
        <PlayerBanner
          id={p1}
          score={p1Score}
          hand={p1Hand}
          deck={p1Deck}
          resources={p1Resources}
          align="left"
          dim={cam === 'p2'}
        />
        <div className="flex flex-col items-center text-amber-300">
          <div className="font-mono text-2xl font-bold">
            R{state.round ?? 0}/{ROUNDS}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-stone-400">
            {state.activePlayer ? `${shortId(state.activePlayer)} to act` : 'in progress'}
          </div>
        </div>
        <PlayerBanner
          id={p2}
          score={p2Score}
          hand={p2Hand}
          deck={p2Deck}
          resources={p2Resources}
          align="right"
          dim={cam === 'p1'}
        />
      </header>

      {/* Realm board — compact horizontal strip with stones in their dock */}
      <main className="flex flex-1 flex-col items-center justify-center gap-1 px-4">
        {state.realms.map((realm, idx) => {
          const isActive = (state.round ?? 0) === idx + 1;
          return (
            <div
              key={realm.name}
              className={`flex w-full max-w-3xl items-center gap-2 rounded border px-3 py-2 ${
                isActive
                  ? 'border-amber-500/60 bg-amber-900/20 ring-1 ring-amber-400/40'
                  : 'border-stone-800 bg-stone-900/40'
              }`}
            >
              <RealmHalf
                placements={realm.placements.filter((p) => p.owner === p1)}
                cards={cards}
                align="left"
              />
              <div className="flex-shrink-0 text-center">
                <img
                  src={STONE_TEXTURE[realm.name]}
                  alt={realm.name}
                  className="h-9 w-9"
                  draggable={false}
                />
                <div className="text-[9px] font-mono text-stone-400">{realm.name}</div>
                <div className="text-[9px]">
                  {realm.stone === p1 ? '←' : realm.stone === p2 ? '→' : '·'}
                </div>
              </div>
              <RealmHalf
                placements={realm.placements.filter((p) => p.owner === p2)}
                cards={cards}
                align="right"
              />
            </div>
          );
        })}
      </main>

      {/* Score pip rail — bottom strip showing per-round outcomes */}
      <footer className="flex justify-center gap-1 px-4 py-2">
        {Array.from({ length: ROUNDS }).map((_, i) => {
          const ri = i + 1;
          return (
            <div
              key={ri}
              className={`flex flex-col items-center gap-0.5 rounded px-2 py-0.5 ${
                ri === state.round ? 'bg-amber-900/30' : ''
              }`}
            >
              <Pip won={p1Pips[i] === 1} side="p1" />
              <span className="font-mono text-[9px] text-stone-500">R{ri}</span>
              <Pip won={p2Pips[i] === 1} side="p2" />
            </div>
          );
        })}
      </footer>

      {/* Suppress unused-viewer warning in broadcast mode (caster has no
          first-person perspective). */}
      {viewer === '' ? null : null}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function PlayerBanner({
  id,
  score,
  hand,
  deck,
  resources,
  align,
  dim,
}: {
  id: PlayerId;
  score: number;
  hand: number;
  deck: number;
  resources: { energy: number; actions: number; hoard: number };
  align: 'left' | 'right';
  dim: boolean;
}): ReactElement {
  const flex = align === 'left' ? 'flex-row' : 'flex-row-reverse';
  return (
    <div className={`flex ${flex} items-center gap-3 ${dim ? 'opacity-30' : ''}`}>
      <div
        className={`font-mono text-2xl font-bold ${align === 'left' ? 'text-left' : 'text-right'}`}
      >
        {score}
      </div>
      <div className={`text-xs ${align === 'left' ? 'text-left' : 'text-right'}`}>
        <div className="font-semibold">{shortId(id)}</div>
        <div className="font-mono text-[10px] text-stone-400">
          {hand} hand · {deck} deck · ⚡{resources.energy} 🎯{resources.actions}
        </div>
      </div>
    </div>
  );
}

function RealmHalf({
  placements,
  cards,
  align,
}: {
  placements: readonly { cardId: string; tapped: boolean; faceDown: boolean }[];
  cards: ReadonlyMap<string, CardData>;
  align: 'left' | 'right';
}): ReactElement {
  return (
    <div
      className={`flex min-h-8 flex-1 gap-1 ${align === 'left' ? 'justify-end' : 'justify-start'}`}
    >
      {placements.length === 0 ? (
        <span className="text-[10px] italic text-stone-600">empty</span>
      ) : (
        placements.slice(0, 5).map((p) => (
          <span
            key={p.cardId}
            className={`flex h-7 w-5 items-center justify-center rounded-sm bg-amber-900/60 text-[7px] font-mono leading-tight ring-1 ring-amber-700/40 ${
              p.tapped ? 'rotate-90' : ''
            }`}
            title={cards.get(p.cardId)?.name ?? p.cardId}
          >
            {p.faceDown ? '?' : (cards.get(p.cardId)?.name ?? p.cardId).slice(0, 2)}
          </span>
        ))
      )}
    </div>
  );
}

function Pip({ won, side }: { won: boolean; side: 'p1' | 'p2' }): ReactElement {
  return (
    <div
      className={`h-2 w-2 rounded-full ${
        won ? (side === 'p1' ? 'bg-amber-400' : 'bg-rose-400') : 'bg-stone-700'
      }`}
    />
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function shortId(p: PlayerId): string {
  return p.length > 14 ? `${p.slice(0, 8)}…${p.slice(-3)}` : p;
}

function handCount(state: PublicTableState, p: PlayerId): number {
  const z = state.zones[p];
  if (!z) return 0;
  if ('hand' in z && Array.isArray(z.hand)) return z.hand.length;
  if ('handCount' in z && typeof z.handCount === 'number') return z.handCount;
  return 0;
}

function zoneCount(state: PublicTableState, p: PlayerId, kind: 'deck'): number {
  const z = state.zones[p];
  if (!z) return 0;
  if (kind in z && Array.isArray((z as Record<string, unknown>)[kind])) {
    return ((z as Record<string, unknown>)[kind] as readonly unknown[]).length;
  }
  if ('deckCount' in z && typeof (z as { deckCount?: number }).deckCount === 'number') {
    return (z as { deckCount: number }).deckCount;
  }
  return 0;
}
